import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { partialMatchKey, useQueryClient } from '@tanstack/react-query';
import { patientNotesQueryKeyPrefix } from '../../../../hooks/queries/usePatientNotes';
import { useAuth0 } from '@auth0/auth0-react';
import { NotesPanel } from '../../../notes/NotesPanel';
import notesService from '../../../../services/notesService';
import { useAuth } from '../../../../hooks/useAuth';

type Category = 'all' | 'clinical' | 'billing' | 'delivery' | 'general' | 'packaging' | 'verification';

interface PatientNotesPanelProps {
  patientId: string;
  defaultTab?: Category;
  compact?: boolean;
  maxHeight?: string;
  showHeader?: boolean;
  className?: string;
  /** Only show these tabs (if omitted, shows all) */
  visibleTabs?: Category[];
  /** Auto-apply these tags to new notes */
  defaultTags?: string[];
  /** Order ID to automatically tag new notes with */
  relatedOrderId?: string;
  /** Order number for display in tags */
  orderNumber?: string;
  /** Rx number(s) to automatically tag new notes with */
  relatedPrescriptionId?: string;
  /** If true, auto-set showOnOrder for new notes */
  showOnOrder?: boolean;
  /** Callback when unsaved note state changes */
  onUnsavedChange?: (hasUnsaved: boolean) => void;
  /** Callback with the count of (non-deleted) notes currently shown — e.g. for a badge */
  onCountChange?: (count: number) => void;
}

const ALL_TABS: { key: Category; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'clinical', label: 'Clinical' },
  { key: 'packaging', label: 'Packaging' },
  { key: 'verification', label: 'Verification' },
  { key: 'billing', label: 'Billing' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'general', label: 'General' },
];

const TYPE_MAP: Record<Category, string[]> = {
  all: [
    'clinical', 'alert', 'allergy', 'medication_review', 'counseling',
    'packaging', 'packaging_issue',
    'verification', 'product_check',
    'billing', 'insurance', 'payment', 'troubleshooting',
    'delivery', 'shipping', 'address_change',
    'general', 'follow_up', 'patient_request', 'other',
  ],
  clinical: ['clinical', 'alert', 'allergy', 'medication_review', 'counseling'],
  packaging: ['packaging', 'packaging_issue'],
  verification: ['verification', 'product_check'],
  billing: ['billing', 'insurance', 'payment', 'troubleshooting'],
  delivery: ['delivery', 'shipping', 'address_change'],
  general: ['general', 'follow_up', 'patient_request', 'other'],
};

const DEFAULT_TYPE: Record<Category, string> = {
  all: 'general',
  clinical: 'clinical',
  packaging: 'packaging',
  verification: 'verification',
  billing: 'billing',
  delivery: 'delivery',
  general: 'general',
};

export const PatientNotesPanel: React.FC<PatientNotesPanelProps> = ({
  patientId,
  defaultTab = 'all',
  compact = false,
  maxHeight,
  showHeader = true,
  className,
  visibleTabs,
  defaultTags,
  relatedOrderId,
  orderNumber,
  relatedPrescriptionId,
  showOnOrder,
  onUnsavedChange,
  onCountChange,
}) => {
  const [category, setCategory] = useState<Category>(defaultTab);
  const { user } = useAuth0();
  const { hasCustomPermission } = useAuth();

  const currentUser = useMemo(() => ({
    id: user?.sub || 'unknown',
    name: user?.name || user?.email || 'Current User',
    role: user?.['https://greenshield.phox.app/roles']?.[0] || 'pharmacist',
  }), [user]);

  const tabs = useMemo(() => {
    if (!visibleTabs) return ALL_TABS;
    return ALL_TABS.filter(t => visibleTabs.includes(t.key));
  }, [visibleTabs]);

  const userRole = currentUser.role.toLowerCase();
  const canWrite = ['admin', 'pharmacist', 'technician', 'manager'].includes(userRole);
  // GP-649: any user holding the DB-backed `editClinicalData` permission may edit/delete
  // ANY note (not just their own). Keep the legacy Auth0 admin/manager string roles as a
  // fallback so no previously-working user loses access if their custom role hasn't loaded
  // yet (hasCustomPermission returns false when customRole is null — graceful, no lockout:
  // the author still falls back to author-only edit via NotesPanel's canEdit).
  const canManageAll =
    hasCustomPermission('editClinicalData') || ['admin', 'manager'].includes(userRole);

  const types = TYPE_MAP[category];

  // `NotesPanel` reloads whenever `fetchNotes` changes identity, so this
  // version is in its dependencies. It moves on ONE event: an invalidation of
  // this patient's notes prefix in the query cache — what a permanent-note save
  // on the Patient info tab issues (PRX-80 Task 9).
  //
  // A listener, not a query of its own (fix round 3): fix round 2's "signal"
  // query put its `dataUpdatedAt` here, which moves from 0 a microtask after
  // mount, so every one of this panel's ~20 mounts loaded twice with two loads
  // in flight and nothing ordering them.
  //
  // What it can hear: TanStack dispatches an invalidation to the queries the
  // cache already holds under the prefix that are not already marked invalid.
  // Today that is only the All Notes grid's `all-notes` query
  // (`ComprehensiveNotesPanel`; `usePatientNotes` shares the prefix but no
  // component calls it). With none there, there is no event — and the only
  // invalidator, the permanent-note card, is never mounted beside this panel;
  // a panel mounted after the save loads fresh anyway.
  const queryClient = useQueryClient();
  const [notesVersion, setNotesVersion] = useState(0);
  useEffect(() => {
    if (!patientId) return undefined;
    const prefix = patientNotesQueryKeyPrefix(patientId);
    return queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'invalidate' &&
        partialMatchKey(event.query.queryKey, prefix)
      ) {
        setNotesVersion((version) => version + 1);
      }
    });
  }, [queryClient, patientId]);

  const fetchNotes = useCallback(async (opts: any) => {
    const all = await notesService.fetchNotesForPanel(opts);
    if (category === 'all') return all;
    return all.filter((n: any) => types.includes(n.type || 'general'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, types, notesVersion]);

  const createNote = useCallback(async (opts: any) => {
    return notesService.createNoteForPanel({
      ...opts,
      type: opts.type || DEFAULT_TYPE[category],
      tags: [...(opts.tags || []), ...(defaultTags || [])],
      ...(relatedOrderId ? { relatedOrderId } : {}),
      ...(relatedPrescriptionId ? { relatedPrescriptionId } : {}),
      ...(relatedOrderId ? { showOnOrder: showOnOrder ?? true } : {}),
      ...(relatedOrderId ? { workflowStage: 'counseling' } : {}),
    });
  }, [category, defaultTags, relatedOrderId, relatedPrescriptionId, showOnOrder]);

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <nav className="notes-tabs" aria-label="Note categories">
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            className={`notes-tab ${category === t.key ? 'active' : ''}`}
            onClick={() => setCategory(t.key)}
            aria-current={category === t.key ? 'true' : undefined}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <NotesPanel
        entityType="patient"
        entityId={patientId}
        currentUserId={currentUser.id}
        currentUserName={currentUser.name}
        currentUserRole={currentUser.role}
        canWrite={canWrite}
        canManageAll={canManageAll}
        fetchNotes={fetchNotes}
        createNote={createNote}
        updateNote={notesService.updateNoteForPanel.bind(notesService)}
        deleteNote={notesService.deleteNoteForPanel.bind(notesService)}
        restoreNote={notesService.restoreNoteForPanel.bind(notesService)}
        showTypeFilter={false}
        allowedTypes={types}
        defaultType={DEFAULT_TYPE[category]}
        compact={compact}
        maxHeight={maxHeight || (compact ? '300px' : 'calc(100vh - 400px)')}
        onUnsavedChange={onUnsavedChange}
        onCountChange={onCountChange}
      />
    </div>
  );
};
